/**
 * @fileoverview Alert component for displaying important messages
 * @module ctl-alert
 * @version 1.0.0
 * @deprecated Use ctl-message with variant="alert" instead. This component is maintained for backwards compatibility.
 * 
 * @description
 * An alert component for displaying contextual feedback messages to users.
 * Supports multiple tones (info, success, warning, danger) and variants (soft, solid).
 * 
 * **DEPRECATED**: This component has been replaced by the unified `ctl-message` component.
 * Please use `<ctl-message variant="alert">` instead. This component is retained for backwards compatibility.
 * 
 * @example
 * <!-- DEPRECATED: Old way -->
 * <ctl-alert tone="info">Your profile has been updated successfully.</ctl-alert>
 * 
 * <!-- RECOMMENDED: New way -->
 * <ctl-message variant="alert" tone="info">Your profile has been updated successfully.</ctl-message>
 * 
 * @example
 * <!-- Info alert with soft variant -->
 * <ctl-alert tone="info">
 *   Your profile has been updated successfully.
 * </ctl-alert>
 * 
 * @example
 * <!-- Success alert with solid variant -->
 * <ctl-alert tone="success" variant="solid">
 *   Payment processed successfully!
 * </ctl-alert>
 * 
 * @example
 * <!-- Warning alert with icon -->
 * <ctl-alert tone="warning">
 *   <ctl-icon slot="icon" name="alert-triangle"></ctl-icon>
 *   Your session will expire in 5 minutes.
 * </ctl-alert>
 * 
 * @example
 * <!-- Dismissible alert -->
 * <ctl-alert tone="danger" dismissible>
 *   An error occurred while processing your request.
 * </ctl-alert>
 */

import './ctl-message.js';
import { dispatchEvent } from '../../../utils/events.js';
import { shadowQuery } from '../../../utils/dom.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    display: block;
    position: relative;
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    padding: var(--ctl-space-3) var(--ctl-space-4);
    font: var(--ctl-font-weight-normal) var(--ctl-font-size-base) var(--ctl-font-sans, system-ui);
    line-height: var(--ctl-leading-normal);
  }

  /* Tone colors - solid variant */
  :host([variant="solid"][tone="info"]) {
    background: var(--ctl-color-info);
    color: var(--ctl-color-on-info);
    border-color: color-mix(in srgb, var(--ctl-color-info) 78%, black);
  }
  :host([variant="solid"][tone="success"]) {
    background: var(--ctl-color-success);
    color: var(--ctl-color-on-success);
    border-color: color-mix(in srgb, var(--ctl-color-success) 78%, black);
  }
  :host([variant="solid"][tone="warning"]) {
    background: var(--ctl-color-warning);
    color: var(--ctl-color-on-warning);
    border-color: color-mix(in srgb, var(--ctl-color-warning) 78%, black);
  }
  :host([variant="solid"][tone="danger"]) {
    background: var(--ctl-color-danger);
    color: var(--ctl-color-on-danger);
    border-color: color-mix(in srgb, var(--ctl-color-danger) 78%, black);
  }

  /* Tone colors - soft variant (default) */
  :host(:not([variant]), [variant="soft"]) {
    background: var(--ctl-color-surface);
  }
  :host([variant="soft"][tone="info"]) {
    background: color-mix(in srgb, var(--ctl-color-info) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-info) 25%, var(--ctl-color-border));
  }
  :host([variant="soft"][tone="success"]) {
    background: color-mix(in srgb, var(--ctl-color-success) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-success) 25%, var(--ctl-color-border));
  }
  :host([variant="soft"][tone="warning"]) {
    background: color-mix(in srgb, var(--ctl-color-warning) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-warning) 25%, var(--ctl-color-border));
  }
  :host([variant="soft"][tone="danger"]) {
    background: color-mix(in srgb, var(--ctl-color-danger) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-danger) 25%, var(--ctl-color-border));
  }

  /* Tone colors - outline variant */
  :host([variant="outline"]) {
    background: transparent;
  }
  :host([variant="outline"][tone="info"]) {
    color: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-info) 45%, var(--ctl-color-border));
  }
  :host([variant="outline"][tone="success"]) {
    color: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-success) 45%, var(--ctl-color-border));
  }
  :host([variant="outline"][tone="warning"]) {
    color: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-warning) 45%, var(--ctl-color-border));
  }
  :host([variant="outline"][tone="danger"]) {
    color: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-danger) 45%, var(--ctl-color-border));
  }

  .alert-container {
    display: flex;
    gap: var(--ctl-space-3);
    align-items: start;
  }

  .alert-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-size: var(--ctl-space-5);
  }

  .alert-icon:empty {
    display: none;
  }

  .alert-content {
    flex: 1;
    min-width: 0;
  }

  .alert-title {
    font: var(--ctl-font-weight-semibold) var(--ctl-font-size-lg) var(--ctl-font-sans, system-ui);
    margin: 0 0 var(--ctl-space-2);
  }

  .alert-title:empty {
    display: none;
  }

  .alert-body {
    display: block;
  }

  .alert-actions {
    display: flex;
    gap: var(--ctl-space-2);
    margin-top: var(--ctl-space-3);
  }

  .alert-actions:empty {
    display: none;
  }

  .close-btn {
    flex-shrink: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: var(--ctl-space-1);
    line-height: 1;
    opacity: var(--ctl-opacity-muted);
    transition: opacity var(--ctl-transition-normal);
    color: currentColor;
    font-size: var(--ctl-space-5);
    align-self: start;
  }

  .close-btn:hover {
    opacity: 1;
  }

  :host(:not([closeable])) .close-btn {
    display: none;
  }
</style>

<div class="alert-container" part="container">
  <div class="alert-icon" part="icon">
    <slot name="icon"></slot>
  </div>
  <div class="alert-content" part="content">
    <div class="alert-title" part="title">
      <slot name="title"></slot>
    </div>
    <div class="alert-body" part="body">
      <slot></slot>
    </div>
    <div class="alert-actions" part="actions">
      <slot name="actions"></slot>
    </div>
  </div>
  <button class="close-btn" aria-label="Close" part="close-button">×</button>
</div>
`;

/**
 * Alert Web Component
 * 
 * @class CtlAlert
 * @extends HTMLElement
 * 
 * @attr {string} tone - Alert semantic tone: "info" | "success" | "warning" | "danger" (default: "info")
 * @attr {string} variant - Visual style variant: "soft" | "solid" (default: "soft")
 * @attr {string} title - Optional alert title
 * @attr {boolean} closeable - Makes alert dismissible with close button
 * 
 * @slot - Default slot for alert message content
 * @slot icon - Leading icon slot
 * @slot title - Title content slot (alternative to title attribute)
 * @slot actions - Action buttons or links slot
 * 
 * @fires ctl-close - Dispatched when the alert is closed. Event detail may include a `reason` property indicating why the alert was closed (e.g., "button" when closed via the close button).
 * 
 * @csspart container - The main alert container
 * @csspart icon - The icon wrapper
 * @csspart title - The title element
 * @csspart content - The content wrapper
 * @csspart body - The body content element
 * @csspart actions - The actions container
 * @csspart close-button - The close button
 * 
 * @cssprop --ctl-alert-bg - Alert background color
 * @cssprop --ctl-alert-text - Alert text color
 * @cssprop --ctl-alert-border - Alert border color
 * @cssprop --ctl-alert-radius - Alert border radius (default: 12px)
 * @cssprop --ctl-color-info - Info tone color (default: #1565c0)
 * @cssprop --ctl-color-success - Success tone color (default: #218838)
 * @cssprop --ctl-color-warning - Warning tone color (default: #8d6e63)
 * @cssprop --ctl-color-danger - Danger tone color (default: #b00020)
 */
export class CtlAlert extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  static get observedAttributes() {
    return ["tone", "variant", "title", "closeable"];
  }

  connectedCallback() {
    this._closeHandler = () => this.close();
    const closeBtn = shadowQuery(this, ".close-btn");
    closeBtn.addEventListener("click", this._closeHandler);

    // Set defaults
    if (!this.hasAttribute("tone")) this.setAttribute("tone", "info");
    if (!this.hasAttribute("variant")) this.setAttribute("variant", "soft");

    // Set title element if title attribute exists
    this._updateTitle();

    // Set role for accessibility
    this._updateRole();
  }

  disconnectedCallback() {
    const closeBtn = shadowQuery(this, ".close-btn");
    if (closeBtn && this._closeHandler) {
      closeBtn.removeEventListener("click", this._closeHandler);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "title") {
      this._updateTitle();
    } else if (name === "tone" && oldValue !== newValue) {
      this._updateRole();
    }
  }

  _updateRole() {
    const tone = this.getAttribute("tone");
    if (tone === "danger" || tone === "warning") {
      this.setAttribute("role", "alert");
    } else {
      this.setAttribute("role", "status");
    }
  }

  _updateTitle() {
    const titleAttr = this.getAttribute("title");
    
    // If there is a slotted title element, prefer that and do not manage textContent here.
    if (this.querySelector('[slot="title"]')) {
      return;
    }

    const titleElement = shadowQuery(this, ".alert-title");
    if (titleElement) {
      // When titleAttr is null or empty, clear any previous textContent.
      titleElement.textContent = titleAttr || "";
    }
  }

  close(reason = "user") {
    dispatchEvent(this, "ctl-close", { reason });
    this.remove();
  }
}

customElements.define("ctl-alert", CtlAlert);
